import React from 'react';
import { FormControlLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { SchemaFieldType } from '../../../../../API';
import { useLocales, useTheme } from '../../../../../hooks';
import AssetBrowser from '../../../../Assets/AssetBrowser';
import { AssetTypesEnum } from '../../../../../utils/assetTypes';
import { LokaliseAutocomplete } from '../../../../shared/LokaliseAutocomplete';

export const dynamicFieldTestIds = {
  textField: (key: string): string => `dynamic-field.text.${key}`,
  numberField: (key: string): string => `dynamic-field.number.${key}`,
  longTextField: (key: string): string => `dynamic-field.long-text.${key}`,
  jsonField: (key: string): string => `dynamic-field.json.${key}`,
  booleanField: (key: string): string => `dynamic-field.boolean.${key}`,
  selectField: (key: string): string => `dynamic-field.select.${key}`,
  imageField: (key: string): string => `dynamic-field.image.${key}`,
  lokaliseField: (key: string): string => `dynamic-field.lokalise.${key}`
};

export interface DynamicFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  fieldKey: string;
  fieldDef: { type: SchemaFieldType; required: boolean; options?: string[] };
}

export function DynamicField({ control, fieldKey, fieldDef }: DynamicFieldProps): JSX.Element {
  const name = `data.${fieldKey}` as const;
  const required = fieldDef.required;
  const { t } = useLocales();
  const { formControlColor } = useTheme();

  switch (fieldDef.type) {
    case SchemaFieldType.BOOLEAN:
      return (
        <Controller
          control={control}
          name={name}
          defaultValue={false}
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              label={`${fieldKey}${required ? ' *' : ''}`}
              data-testid={dynamicFieldTestIds.booleanField(fieldKey)}
              control={
                <Switch color={formControlColor} checked={!!value} onChange={(e) => onChange(e.target.checked)} />
              }
            />
          )}
        />
      );

    case SchemaFieldType.SELECT:
      return (
        <>
          <Typography variant="caption">
            {fieldKey}
            {required ? ' *' : ''}
          </Typography>
          <Controller
            control={control}
            name={name}
            rules={{ required: required ? t('layouts.dynamic_module.field_required', { field: fieldKey }) : false }}
            render={({ field }) => (
              <Select
                {...field}
                label={fieldKey}
                required={required}
                value={field.value ?? ''}
                size="small"
                displayEmpty
                fullWidth
                color={formControlColor}
                data-testid={dynamicFieldTestIds.selectField(fieldKey)}
              >
                <MenuItem value="">
                  <em>{t('layouts.dynamic_module.none')}</em>
                </MenuItem>
                {(fieldDef.options ?? []).map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </>
      );

    case SchemaFieldType.NUMBER:
      return (
        <Controller
          control={control}
          name={name}
          rules={{ required: required ? t('layouts.dynamic_module.field_required', { field: fieldKey }) : false }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={fieldKey}
              required={required}
              value={field.value ?? ''}
              type="number"
              size="small"
              fullWidth
              error={!!error}
              helperText={error?.message}
              onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
              color={formControlColor}
              data-testid={dynamicFieldTestIds.numberField(fieldKey)}
            />
          )}
        />
      );

    case SchemaFieldType.LONG_TEXT:
      return (
        <Controller
          control={control}
          name={name}
          rules={{ required: required ? t('layouts.dynamic_module.field_required', { field: fieldKey }) : false }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={fieldKey}
              required={required}
              value={field.value ?? ''}
              multiline
              rows={4}
              size="small"
              fullWidth
              error={!!error}
              helperText={error?.message}
              color={formControlColor}
              data-testid={dynamicFieldTestIds.longTextField(fieldKey)}
            />
          )}
        />
      );

    case SchemaFieldType.JSON:
      return (
        <Controller
          control={control}
          name={name}
          rules={{
            required: required ? t('layouts.dynamic_module.field_required', { field: fieldKey }) : false,
            validate: (v) => {
              if (!v) return true;
              try {
                JSON.parse(String(v));
                return true;
              } catch {
                return t('layouts.dynamic_module.field_invalid_json', { field: fieldKey });
              }
            }
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={fieldKey}
              required={required}
              value={field.value ?? ''}
              multiline
              rows={4}
              size="small"
              fullWidth
              placeholder="{}"
              error={!!error}
              helperText={error?.message}
              color={formControlColor}
              data-testid={dynamicFieldTestIds.jsonField(fieldKey)}
            />
          )}
        />
      );

    case SchemaFieldType.IMAGE:
      return (
        <>
          <Typography variant="caption">
            {fieldKey}
            {required ? ' *' : ''}
          </Typography>
          <Controller
            control={control}
            name={name}
            rules={{ required: required ? t('layouts.dynamic_module.field_required', { field: fieldKey }) : false }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <AssetBrowser
                assetType={`dynamic-module/${fieldKey}` as AssetTypesEnum}
                value={value as string}
                hasError={!!error}
                onChange={onChange}
                data-testid={dynamicFieldTestIds.imageField(fieldKey)}
              />
            )}
          />
        </>
      );

    case SchemaFieldType.LOKALISE_KEY:
      return (
        <Controller
          control={control}
          name={name}
          rules={{ required: required ? t('layouts.dynamic_module.field_required', { field: fieldKey }) : false }}
          render={({ field: { value, onChange } }) => (
            <LokaliseAutocomplete
              name={fieldKey}
              required={required}
              label={fieldKey}
              value={(value as string) ?? ''}
              onChange={onChange}
              data-testid={dynamicFieldTestIds.lokaliseField(fieldKey)}
            />
          )}
        />
      );

    // Text → text field
    default:
      return (
        <Controller
          control={control}
          name={name}
          rules={{ required: required ? t('layouts.dynamic_module.field_required', { field: fieldKey }) : false }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={fieldKey}
              required={required}
              value={field.value ?? ''}
              size="small"
              fullWidth
              error={!!error}
              helperText={error?.message}
              color={formControlColor}
              data-testid={dynamicFieldTestIds.textField(fieldKey)}
            />
          )}
        />
      );
  }
}
